import cx from "classnames";
import { useRef } from "react";
import { NavLink } from "react-router-dom";

import { LanguageSwitcher } from "@/core/i18n/LanguageSwitcher/LanguageSwitcher";
import { useTranslation } from "@/core/i18n";
import { NavRailIcon, navRailItemClass } from "molecules/navigation/nav-rail/NavRail";
import { handleLinkClick } from "utils/androidByPassDeepLinkClick";

import { type AccountLink, useAccountLinks } from "./useAccountLinks";

const RailItem = ({ link }: { link: AccountLink }) => {
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const icon = <NavRailIcon svg={link.icon} />;

  if (link.href) {
    return (
      <a
        ref={anchorRef}
        href={link.href}
        target="_self"
        rel="noopener noreferrer"
        className={navRailItemClass(false)}
        onClick={() => handleLinkClick(anchorRef)}
      >
        {icon}
        <span className="flex-1">{link.label}</span>
      </a>
    );
  }

  if (link.to) {
    return (
      <NavLink to={link.to} className={({ isActive }) => navRailItemClass(isActive)}>
        {icon}
        <span className="flex-1">{link.label}</span>
      </NavLink>
    );
  }

  return (
    <button type="button" className={navRailItemClass(false)} onClick={link.onClick}>
      {icon}
      <span className="flex-1">{link.label}</span>
    </button>
  );
};

/**
 * Account navigation as a persistent desktop rail.
 *
 * On a phone these entries are a full-screen list you drill into and back out
 * of; on a desktop that costs a whole screen transition to change one setting.
 * The rail keeps the whole menu visible while the chosen page fills the pane
 * beside it.
 */
export const AccountNavRail = ({ className }: { className?: string }) => {
  const { t } = useTranslation();
  const links = useAccountLinks();
  const groups: AccountLink["group"][] = ["profile", "main", "admin", "logout"];

  return (
    <nav className={cx("flex flex-col gap-6", className)}>
      <h2 className="px-3 text-sub2 uppercase text-secondary">{t("account.header")}</h2>

      {groups.map((group) => {
        const groupLinks = links.filter((link) => link.group === group);
        if (groupLinks.length === 0) return null;

        return (
          <div key={group} className="flex flex-col gap-0.5">
            {groupLinks.map((link) => (
              <RailItem key={link.key} link={link} />
            ))}
            {group === "main" && (
              // The switcher's row is styled for the phone settings list
              // (solid white, list padding). In the rail that reads as a
              // permanently-selected item, so it is normalised to match the
              // other rail entries.
              <div className="[&_button:hover]:bg-background-two [&_button]:rounded-lg [&_button]:bg-transparent [&_button]:px-3 [&_button]:py-2.5 [&_button]:text-body2">
                <LanguageSwitcher />
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
};
